import React, { useState, useCallback } from 'react';
import usePoll from '../hooks/usePoll';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MdArrowBack, MdVerified, MdAccessTime,
  MdLocationOn, MdStar, MdBed, MdPerson,
} from 'react-icons/md';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ReviewDisplayCard } from '../components/ReviewRatingSection';

// ── Hospital open/closed status ───────────────────────────────────────────────
function getStatus(h) {
  if (h.isShutdown) return {
    label: `🔴 Temporarily Closed · ${h.shutdownReason || 'Maintenance'}`,
    color: 'var(--danger)', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.25)', blocked: true,
  };
  if (h.is24Hours || h.is24x7) return {
    label: '🟢 Open 24/7 — Always accepting patients',
    color: 'var(--success)', bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.25)', blocked: false,
  };
  const days  = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const today = days[new Date().getDay()];
  const opDays = h.opDays || ['Mon','Tue','Wed','Thu','Fri','Sat'];
  const isToday = opDays.includes(today);
  const now  = new Date();
  const nowM = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = (h.opStart || '08:00').split(':').map(Number);
  const [eh, em] = (h.opEnd   || '18:00').split(':').map(Number);
  const isOpen = isToday && nowM >= sh*60+sm && nowM < eh*60+em;
  if (isOpen) return {
    label: `🟢 Open Now · OP hours ${h.opStart}–${h.opEnd}`,
    color: 'var(--success)', bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.25)', blocked: false,
  };
  if (isToday) return {
    label: `🕐 Closed · Reopens at ${h.opStart}`,
    color: 'var(--warning)', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', blocked: false,
  };
  return {
    label: `🌙 No OP Today · Days: ${opDays.join(', ')}`,
    color: 'var(--warning)', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', blocked: false,
  };
}

// ── Patient details modal (matches Flutter PatientDetailsScreen) ───────────────
function PatientDetailsModal({ title, onSubmit, onClose, prefillName }) {
  const [name,   setName]   = useState(prefillName || '');
  const [age,    setAge]    = useState('');
  const [gender, setGender] = useState('Male');
  const [addr,   setAddr]   = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return alert('Patient name is required');
    if (!age || isNaN(age) || +age <= 0 || +age > 130) return alert('Enter a valid age');
    onSubmit({ name: name.trim(), age: +age, gender, address: addr.trim() });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 1000, backdropFilter: 'blur(6px)',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#0f1629', borderRadius: '20px 20px 0 0',
        padding: 24, width: '100%', maxWidth: 520,
        border: '1px solid var(--border)', borderBottom: 'none',
        animation: 'slideUp 0.3s ease',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17 }}>{title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Fill in patient information</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>

        <form onSubmit={submit}>
          {/* Name */}
          <div className="input-group">
            <label className="input-label">Patient Name *</label>
            <input className="input" placeholder="Enter full name"
              value={name} onChange={e => setName(e.target.value)} required />
          </div>

          {/* Age */}
          <div className="input-group">
            <label className="input-label">Age *</label>
            <input className="input" type="number" placeholder="e.g. 35" min="1" max="130"
              value={age} onChange={e => setAge(e.target.value)} required />
          </div>

          {/* Gender */}
          <div className="input-group">
            <label className="input-label">Gender *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['Male', 'Female', 'Other'].map(g => (
                <button key={g} type="button" onClick={() => setGender(g)} style={{
                  flex: 1, padding: '10px 8px', borderRadius: 10, border: '1px solid',
                  borderColor: gender === g ? 'var(--primary)' : 'var(--border)',
                  background: gender === g ? 'var(--primary-glow)' : 'var(--bg-card)',
                  color: gender === g ? 'var(--primary)' : 'var(--text-muted)',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s',
                }}>
                  {g === 'Male' ? '♂ Male' : g === 'Female' ? '♀ Female' : '⚧ Other'}
                </button>
              ))}
            </div>
          </div>

          {/* Address */}
          <div className="input-group">
            <label className="input-label">Address (optional)</label>
            <input className="input" placeholder="Street, City"
              value={addr} onChange={e => setAddr(e.target.value)} />
          </div>

          <button type="submit" className="btn btn-primary btn-full btn-lg" style={{ marginTop: 8 }}>
            Continue →
          </button>
        </form>
      </div>
    </div>
  );
}

// ── OfferRibbon ──────────────────────────────────────────────────────────────
// Horizontal-scrolling card list used for Offers and Health Camps. Hides its own
// scrollbar but keeps native touch / wheel scrolling. Cards fall back to a
// gradient cover when no `imageUrl` is supplied so the ribbon never looks broken.
export function OfferRibbon({ title, items, accent = 'var(--red, #d32f2f)', onTap }) {
  // PERFORMANCE (May 27 2026): preload the FIRST banner image as soon as
  // OfferRibbon mounts so the browser fetches it at high priority — gives
  // the same ~700 ms win as the Flutter app's _warmBannerCache.
  React.useEffect(() => {
    if (!items || items.length === 0) return;
    const first = items.find(o => o && o.imageUrl);
    if (!first) return;
    // Skip if already preloaded for this URL.
    const id = `hcen-preload-${encodeURIComponent(first.imageUrl).slice(0, 80)}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'preload';
    link.as = 'image';
    link.href = first.imageUrl;
    link.fetchPriority = 'high';
    document.head.appendChild(link);
  }, [items]);

  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{title}</div>
      <div
        style={{
          display: 'flex', gap: 12, overflowX: 'auto', overflowY: 'hidden',
          paddingBottom: 4, scrollSnapType: 'x mandatory',
          // Hide scrollbar
          scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}
      >
        {items.map((o, idx) => (
          <div
            key={o.offerId || o.id || o.title}
            onClick={() => onTap && onTap(o)}
            style={{
              flex: '0 0 240px', scrollSnapAlign: 'start',
              borderRadius: 14, overflow: 'hidden',
              border: '1px solid var(--border, rgba(255,255,255,0.08))',
              background: 'var(--bg-card, #fff)',
              cursor: onTap ? 'pointer' : 'default',
              boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 22px rgba(0,0,0,0.12)'; }}
            onMouseOut={e  => { e.currentTarget.style.transform = 'none';            e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.08)'; }}
          >
            <div style={{
              height: 110, position: 'relative',
              // Gradient fallback shows immediately while the <img> below loads.
              background: `linear-gradient(135deg, ${accent}, rgba(0,0,0,0.45))`,
            }}>
              {o.imageUrl && (
                <img
                  src={o.imageUrl}
                  alt={o.title || ''}
                  width="240"
                  height="110"
                  // First card: load eagerly with HIGH priority (gives the
                  // browser the same priority bump as Flutter's
                  // CachedNetworkImageProvider warm-up).
                  loading={idx === 0 ? 'eager' : 'lazy'}
                  fetchpriority={idx === 0 ? 'high' : 'auto'}
                  decoding="async"
                  onError={e => { e.currentTarget.style.display = 'none'; }}
                  style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    objectFit: 'cover',
                    // Fade in once decoded so the swap from gradient is smooth
                    // (not a jarring flash like the old CSS background did).
                    opacity: 0, transition: 'opacity 0.18s ease-out',
                  }}
                  onLoad={e => { e.currentTarget.style.opacity = 1; }}
                />
              )}
              {o.badge && (
                <span style={{
                  position: 'absolute', top: 8, left: 8,
                  background: o.badgeColor || accent, color: '#fff',
                  padding: '3px 9px', fontSize: 10, fontWeight: 800,
                  borderRadius: 999, letterSpacing: 0.4, textTransform: 'uppercase',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                  zIndex: 1,
                }}>{o.badge}</span>
              )}
            </div>
            <div style={{ padding: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e', marginBottom: 4, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {o.title}
              </div>
              {o.description && (
                <div style={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {o.description}
                </div>
              )}
              {o.validUntil && (
                <div style={{ marginTop: 6, fontSize: 10.5, color: accent, fontWeight: 700 }}>
                  Valid till {o.validUntil}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main HospitalDetailPage ───────────────────────────────────────────────────
export default function HospitalDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, showToast } = useAuth();

  const [hospital,  setHospital]  = useState(null);
  const [doctors,   setDoctors]   = useState([]);
  const [offers,    setOffers]    = useState([]);  // approved offers/camps for THIS hospital
  const [reviews,   setReviews]   = useState([]);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(null); // 'walkin' | 'doctor:{id}'

  // Background-poll hospital + doctors + offers every 2.5s so doctor on/off-duty
  // status, fee changes and new health offers appear in near-real-time without
  // a manual refresh. Offers are non-blocking — failure must not prevent the
  // page from rendering.
  usePoll(useCallback(async () => {
    const [hr, dr, or, rv, rs] = await Promise.all([
      api.getHospitals(),
      api.getDoctors(id),
      api.getOffers({ hospitalId: id }),
      api.hospitalReviews(id, 6),
      api.hospitalReviewSummary(id),
    ]);
    const list = hr.data || [];
    const h = list.find(x => (x.id || x.hospitalId) === id);
    setHospital(h || null);
    const docList = dr.data?.doctors ?? dr.data ?? [];
    setDoctors(Array.isArray(docList) ? docList : []);
    setOffers(Array.isArray(or.data) ? or.data : []);
    setReviews(Array.isArray(rv.data?.reviews) ? rv.data.reviews : []);
    setReviewSummary(rs.data || null);
    setLoading(false);
  }, [id]), 2500);

  // Split offers vs health-camps by badge/title heuristic. Backend uses a
  // single HealthOffers table for both — the Flutter app surfaces them in two
  // ribbons and we want the same visual hierarchy here.
  const isCamp = (o) => /camp|drive|checkup/i.test((o.badge || '') + ' ' + (o.title || ''));
  const camps  = offers.filter(isCamp);
  const promos = offers.filter(o => !isCamp(o));

  if (loading) return <div className="page"><div className="spinner" /></div>;
  if (!hospital) return (
    <div className="page">
      <div className="empty">
        <div className="empty-icon">🏥</div>
        <div className="empty-title">Hospital not found</div>
        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/hospitals')}>
          Go Back
        </button>
      </div>
    </div>
  );

  const st = getStatus(hospital);

  const handleBookWalkin = () => {
    if (st.blocked) { showToast(hospital.shutdownReason || 'Hospital temporarily closed', 'error'); return; }
    if (!user) { navigate('/login'); return; }
    setModal('walkin');
  };

  const handleBookDoctor = (doctor) => {
    if (!user) { navigate('/login'); return; }
    setModal('doctor:' + (doctor.id || doctor.doctorId));
  };

  const handlePatientDetails = (details) => {
    setModal(null);
    if (modal === 'walkin') {
      navigate(`/book/op/${id}`, { state: { hospital, patientDetails: details } });
    } else if (modal?.startsWith('doctor:')) {
      const docId = modal.replace('doctor:', '');
      const doctor = doctors.find(d => (d.id || d.doctorId) === docId);
      navigate(`/book/doctor/${docId}`, { state: { doctor, hospital, patientDetails: details } });
    }
  };

  return (
    <div className="fade-in">
      {/* ── Top bar ───────────────────────────────────────────── */}
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 12,
        background: 'rgba(255,255,255,0.02)',
        position: 'sticky', top: 0, zIndex: 10, backdropFilter: 'blur(12px)',
      }}>
        <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm" style={{ padding: 8 }}>
          <MdArrowBack size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 17 }}>{hospital.name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <MdLocationOn size={12} /> {hospital.address || hospital.city || 'HCEN Network'}
          </div>
        </div>
        {hospital.isTrusted && <MdVerified style={{ color: '#f59e0b', fontSize: 22 }} />}
      </div>

      <div className="page">

        {/* ── Hospital Hero (Zomato/Swiggy style poster) ──────── */}
        {(() => {
          const img = hospital.photoUrl || hospital.logoUrl || (Array.isArray(hospital.imageUrls) && hospital.imageUrls[0]);
          return (
            <div style={{
              position: 'relative',
              height: 180,
              borderRadius: 16,
              marginBottom: 20,
              overflow: 'hidden',
              background: img ? `url(${img}) center/cover` : 'linear-gradient(135deg, #4f8ef7, #00b894)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            }}>
              {/* Dark gradient overlay for text contrast */}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.05) 100%)',
              }} />
              {/* Bottom title block */}
              <div style={{ position: 'absolute', left: 16, right: 16, bottom: 12, color: '#fff' }}>
                <div style={{ fontSize: 22, fontWeight: 800, textShadow: '0 2px 8px rgba(0,0,0,0.5)', lineHeight: 1.2 }}>
                  {hospital.name}
                </div>
                <div style={{ fontSize: 12, opacity: 0.92, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <MdLocationOn size={13} /> {hospital.city || hospital.address || 'HCEN Network'}
                </div>
              </div>
              {/* Verified badge */}
              {hospital.isTrusted && (
                <div style={{
                  position: 'absolute', top: 12, right: 12,
                  background: 'rgba(245,158,11,0.95)', color: '#fff',
                  padding: '4px 10px', borderRadius: 14, fontSize: 11, fontWeight: 800,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <MdVerified size={13} /> TRUSTED
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Status banner ─────────────────────────────────── */}
        <div style={{
          padding: '12px 16px', borderRadius: 12, marginBottom: 20,
          background: st.bg, border: `1px solid ${st.border}`,
        }}>
          <div style={{ color: st.color, fontWeight: 700, fontSize: 14 }}>{st.label}</div>
        </div>

        {/* ── Stats row ─────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'ICU Beds', icon: <MdBed />,
              value: `${hospital.icuAvailable ?? 0}/${hospital.icuTotal ?? 0}`,
              color: (hospital.icuAvailable ?? 0) > 0 ? 'var(--success)' : 'var(--danger)' },
            { label: 'Rating', icon: <MdStar />,
              value: Number(hospital.rating) > 0 ? Number(hospital.rating).toFixed(1) : '—',
              color: '#f59e0b' },
            { label: 'Type', icon: <MdPerson />,
              value: hospital.type || 'Private',
              color: 'var(--secondary)' },
          ].map(s => (
            <div key={s.label} className="glass glass-p" style={{ textAlign: 'center', padding: 14 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── OP Hours ──────────────────────────────────────── */}
        {hospital.opStart && (
          <div className="glass glass-p" style={{ marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
            <MdAccessTime style={{ color: '#f59e0b', fontSize: 22, flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                OP Hours: {hospital.opStart} – {hospital.opEnd}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                Days: {(hospital.opDays || ['Mon–Sat']).join(', ')}
              </div>
            </div>
          </div>
        )}

        {/* ── Health Camps (ribbon) ──────────────────────────── */}
        {camps.length > 0 && (
          <OfferRibbon
            title="🏕️ Health Camps & Free Checkups"
            items={camps}
            accent="#10b981"
            onTap={(o) => showToast(o.description || o.title, 'info')}
          />
        )}

        {/* ── Offers / Promotions (ribbon) ──────────────────── */}
        {promos.length > 0 && (
          <OfferRibbon
            title="🎁 Offers & Promotions"
            items={promos}
            accent="var(--red, #d32f2f)"
            onTap={(o) => showToast(o.description || o.title, 'info')}
          />
        )}

        {/* ── Facilities / Departments ───────────────────────  */}
        {hospital.departments?.length > 0 && (
          <div className="glass glass-p" style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>🏥 Departments</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {hospital.departments.map(d => (
                <span key={d} className="badge badge-primary">{d}</span>
              ))}
            </div>
          </div>
        )}

        {/* ── Booking Buttons ────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          {/* Walk-in OP */}
          <button
            className={`btn btn-lg btn-full ${st.blocked ? 'btn-secondary' : 'btn-primary'}`}
            onClick={handleBookWalkin}
            style={{ flexDirection: 'column', gap: 6, height: 88, borderRadius: 16 }}>
            <span style={{ fontSize: 28 }}>🏥</span>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Book Walk-in OP</span>
          </button>

          {/* Choose Doctor */}
          <button
            className="btn btn-secondary btn-lg btn-full"
            onClick={() => {
              if (!user) { navigate('/login'); return; }
              document.getElementById('doctors-section')?.scrollIntoView({ behavior: 'smooth' });
            }}
            style={{ flexDirection: 'column', gap: 6, height: 88, borderRadius: 16 }}>
            <span style={{ fontSize: 28 }}>👨‍⚕️</span>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Choose Doctor</span>
          </button>
        </div>

        {/* ── Patient Reviews ────────────────────────────────── */}
        {(reviewSummary?.count > 0 || reviews.length > 0) && (
          <div style={{ marginBottom: 24 }}>
            <div className="section-title">⭐ Patient Reviews</div>
            {reviewSummary?.count > 0 && (
              <div className="glass glass-p" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#f59e0b' }}>
                  {Number(reviewSummary.average || 0).toFixed(1)}
                </div>
                <div>
                  <div style={{ fontWeight: 700 }}>Hospital rating</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {reviewSummary.count} review{reviewSummary.count === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
            )}
            {reviews.map(r => (
              <ReviewDisplayCard key={r.reviewId || r.createdAt} review={r} />
            ))}
          </div>
        )}

        {/* ── Doctors List ───────────────────────────────────── */}
        {doctors.length > 0 && (
          <div id="doctors-section">
            <div className="section-title">👨‍⚕️ Our Doctors</div>
            <div className="section-sub" style={{ marginBottom: 14 }}>{doctors.length} doctor{doctors.length !== 1 ? 's' : ''}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {doctors.map(d => {
                const docId = d.id || d.doctorId;
                return (
                  <div key={docId} className="glass glass-p"
                    style={{ display: 'flex', gap: 14, alignItems: 'center', cursor: 'pointer', transition: 'var(--transition)' }}
                    onClick={() => handleBookDoctor(d)}>
                    <div style={{
                      width: 52, height: 52, borderRadius: 14, background: 'var(--primary-glow)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 26, flexShrink: 0, overflow: 'hidden',
                    }}>
                      {d.photoUrl ? <img src={d.photoUrl} alt="" style={{ width: 52, height: 52, objectFit: 'cover' }} /> : '👨‍⚕️'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>Dr. {d.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--secondary)', marginBottom: 6 }}>{d.specialization}</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <span className={`badge ${(d.available !== false && d.isAvailable !== false) ? 'badge-green' : 'badge-red'}`}>
                          {(d.available !== false && d.isAvailable !== false) ? '● On Duty' : '● Off Duty'}
                        </span>
                        {Number(d.rating) > 0 && (
                          <span className="badge badge-amber">⭐ {Number(d.rating).toFixed(1)}</span>
                        )}
                        {d.experience && <span className="badge badge-blue">{d.experience} yrs</span>}
                        {(d.fee || d.consultationFee) && (
                          <span className="badge badge-amber">₹{d.fee || d.consultationFee}</span>
                        )}
                        {d.qualification && <span className="badge" style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--text-muted)' }}>{d.qualification}</span>}
                      </div>
                    </div>
                    <div style={{ fontSize: 20, color: 'var(--text-muted)' }}>›</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Patient Details Modal ──────────────────────────────── */}
      {modal && (
        <PatientDetailsModal
          title={modal === 'walkin' ? 'Walk-in OP Booking' : 'Book Doctor Appointment'}
          prefillName={user?.name || ''}
          onSubmit={handlePatientDetails}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
